import type { OrganizationGpuJobDto } from "dutyon-server/types";

export type OrganizationGpuHistorySummary = {
  entries: {
    startDayString: string;
    duration: number;
    status: "complete" | "incomplete" | "independent" | "unavailable";
    historyDays: number | null;
    missingAssignments: number | null;
  }[];
};
/** Describe only server-returned completed results. Overlapping history is never added into one total. */
export function summarizeOrganizationGpuHistory(
  jobs: readonly OrganizationGpuJobDto[],
): OrganizationGpuHistorySummary {
  return {
    entries: jobs.map((job) => {
      const entry: OrganizationGpuHistorySummary["entries"][number] = {
        startDayString: job.input.startDayString,
        duration: job.input.duration,
        status: "unavailable",
        historyDays: null,
        missingAssignments: null,
      };
      if (job.status !== "SUCCEEDED" || !job.result) return entry;
      const { history, evaluationScope } = job.result;
      if (history === undefined && evaluationScope === undefined)
        return { ...entry, status: "independent" };
      if (
        !history ||
        evaluationScope !== "history-and-target" ||
        !Number.isSafeInteger(history.dayCount) ||
        history.dayCount < 1 ||
        history.dayCount > 31 ||
        !Number.isSafeInteger(history.missingAssignments) ||
        history.missingAssignments < 0 ||
        history.missingAssignments > history.dayCount * job.input.nurseIds.length ||
        typeof history.complete !== "boolean" ||
        history.complete !== (history.missingAssignments === 0)
      )
        return entry;
      return {
        ...entry,
        status: history.complete ? "complete" : "incomplete",
        historyDays: history.dayCount,
        missingAssignments: history.missingAssignments,
      };
    }),
  };
}

/** A fresh authorization check must precede this comparison; it cannot establish access by itself. */
export function sameOrganizationGpuPreview(
  reviewed: OrganizationGpuJobDto,
  latest: OrganizationGpuJobDto,
) {
  return (
    reviewed.status === "SUCCEEDED" &&
    latest.status === "SUCCEEDED" &&
    reviewed.result !== null &&
    latest.result !== null &&
    JSON.stringify([
      reviewed.id,
      reviewed.app,
      reviewed.teamId,
      reviewed.input,
      reviewed.result,
    ]) === JSON.stringify([latest.id, latest.app, latest.teamId, latest.input, latest.result])
  );
}
